import React from 'react';
import './JobPostingPreview.css';
import {
  MapPin,
  Briefcase,
  Banknote,
  Clock,
  FileText,
  Download,
  ExternalLink,
  GraduationCap,
  Building2,
  Calendar,
  Users,
  Award,
  Paperclip,
} from 'lucide-react';

const JobPostingPreview = ({ formData }) => {
  const getLabel = (field) => {
    if (!field) return null;
    if (typeof field === 'string') return field;
    return field.label || field.value || null;
  };

  const getFileUrl = (fileData) => {
    if (!fileData) return null;
    if (typeof fileData === 'string') {
      if (fileData.startsWith('http')) return fileData;
      return `${process.env.REACT_APP_API_BASE_URL}/${fileData.replace(/\\/g, '/')}`;
    }
    if (fileData instanceof File) return URL.createObjectURL(fileData);
    if (fileData.file instanceof File) return URL.createObjectURL(fileData.file);
    if (fileData.pdf_path) {
      if (fileData.pdf_path.startsWith('http')) return fileData.pdf_path;
      return `${process.env.REACT_APP_API_BASE_URL}/${fileData.pdf_path.replace(/\\/g, '/')}`;
    }
    return null;
  };

  const getFileName = (fileData) => {
    if (!fileData) return 'No Document Attached';
    if (typeof fileData === 'string') return fileData.split('/').pop().split('\\').pop();
    if (fileData.name) return fileData.name;
    if (fileData.label) return fileData.label;
    return 'Attached Document';
  };

  const mainAttachment = formData.jdDocument || formData.selectedJd;

  const formatApplyBy = () => {
    if (!formData.applyByDate) return 'Not Specified';
    const date = new Date(`${formData.applyByDate}T00:00:00`);
    let dateString = date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    if (formData.applyByTime) {
      return `${dateString} at ${formData.applyByTime}`;
    }
    return dateString;
  };

  const formatSimpleDate = (dateString) => {
    if (!dateString) return 'Not Specified';
    const date = new Date(`${dateString}T00:00:00`);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatGraduation = (gradData) => {
    if (!gradData || gradData.length === 0) return '-';
    if (Array.isArray(gradData)) {
      return gradData.map((g) => getLabel(g)).join(', ');
    }
    try {
      const parsed = JSON.parse(gradData);
      if (Array.isArray(parsed)) return parsed.join(', ');
    } catch (e) {}

    return String(gradData);
  };

  const isInternship = getLabel(formData.typeOfWork) === 'Internship';

  return (
    <div className="jpv-pane">
      {/* ═══ Hero Header ═══ */}
      <div className="jpv-hero">
        <div className="jpv-hero-top">
          <div className="jpv-company-avatar">
            {(formData.companyName || 'C')[0].toUpperCase()}
          </div>
          <div className="jpv-hero-text">
            <h2 className="jpv-role">{getLabel(formData.role) || 'Role Title'}</h2>
            <h3 className="jpv-company">
              <Building2 size={13} />
              {formData.companyName || 'Company Name'}
            </h3>
          </div>
        </div>

        <div className="jpv-quick-info">
          <span className="jpv-chip">
            <MapPin size={13} /> {formData.location || 'Location'}
          </span>
          <span className="jpv-chip">
            <Briefcase size={13} /> {getLabel(formData.typeOfWork) || 'Job Type'}
          </span>
          <span className="jpv-chip jpv-chip-highlight">
            <Banknote size={13} /> {formData.ctc || 'Salary / Stipend'}
          </span>
        </div>

        <div className="jpv-apply-by">
          <Clock size={14} />
          <span>Apply by</span>
          <strong>{formatApplyBy()}</strong>
        </div>
      </div>

      {/* ═══ Education Details ═══ */}
      <div className="jpv-section">
        <div className="jpv-section-header">
          <div className="jpv-section-icon">
            <GraduationCap size={15} />
          </div>
          <h4>Education Details</h4>
        </div>

        <div className="jpv-data-grid">
          {isInternship && (
            <div className="jpv-row">
              <div className="jpv-label">
                <Calendar size={12} /> Target Batch
              </div>
              <div className="jpv-value">{formData.batch || '-'}</div>
            </div>
          )}

          <div className="jpv-row">
            <div className="jpv-label">
              <Award size={12} /> Graduation Degree
            </div>
            <div className="jpv-value">{formatGraduation(formData.graduation)}</div>
          </div>

          {formData.tenth && (
            <div className="jpv-row">
              <div className="jpv-label">10th Grade</div>
              <div className="jpv-value">
                <span className="jpv-badge jpv-badge-blue">Min {formData.tenth}%</span>
              </div>
            </div>
          )}

          {formData.twelfth && (
            <div className="jpv-row">
              <div className="jpv-label">12th Grade</div>
              <div className="jpv-value">
                <span className="jpv-badge jpv-badge-teal">Min {formData.twelfth}%</span>
              </div>
            </div>
          )}

          {formData.minCGPA && (
            <div className="jpv-row">
              <div className="jpv-label">Minimum CGPA</div>
              <div className="jpv-value">
                <span className="jpv-badge jpv-badge-purple">{formData.minCGPA}</span>
              </div>
            </div>
          )}

          <div className="jpv-row">
            <div className="jpv-label">
              <Users size={12} /> Gender
            </div>
            <div className="jpv-value">{getLabel(formData.gender) || 'Any'}</div>
          </div>

          {formData.dateOfVisit && (
            <div className="jpv-row">
              <div className="jpv-label">
                <Calendar size={12} /> Date of Visit
              </div>
              <div className="jpv-value">{formatSimpleDate(formData.dateOfVisit)}</div>
            </div>
          )}
        </div>
      </div>

      {/* ═══ About Company ═══ */}
      {formData.aboutCompany && (
        <div className="jpv-section">
          <div className="jpv-section-header">
            <div className="jpv-section-icon jpv-icon-purple">
              <Building2 size={15} />
            </div>
            <h4>About {formData.companyName || 'the Company'}</h4>
          </div>
          <p className="jpv-text">{formData.aboutCompany}</p>
        </div>
      )}

      {/* ═══ About Job ═══ */}
      {formData.aboutJob && (
        <div className="jpv-section">
          <div className="jpv-section-header">
            <div className="jpv-section-icon jpv-icon-amber">
              <Briefcase size={15} />
            </div>
            <h4>About the Job</h4>
          </div>
          <p className="jpv-text">{formData.aboutJob}</p>
        </div>
      )}

      {/* ═══ Additional Info ═══ */}
      {formData.additionalInfo && (
        <div className="jpv-section">
          <div className="jpv-section-header">
            <div className="jpv-section-icon jpv-icon-rose">
              <FileText size={15} />
            </div>
            <h4>Additional Information</h4>
          </div>
          <p className="jpv-text">{formData.additionalInfo}</p>
        </div>
      )}

      {/* ═══ Hiring Workflow ═══ */}
      {formData.hiringWorkflows && (
        <div className="jpv-section">
          <div className="jpv-section-header">
            <div className="jpv-section-icon jpv-icon-indigo">
              <Users size={15} />
            </div>
            <h4>Hiring Workflow</h4>
          </div>
          <p className="jpv-text">{formData.hiringWorkflows}</p>
        </div>
      )}

      {/* ═══ Attachments ═══ */}
      <div className="jpv-section">
        <div className="jpv-section-header">
          <div className="jpv-section-icon">
            <Paperclip size={15} />
          </div>
          <h4>Attachments</h4>
        </div>

        {mainAttachment ? (
          <div className="jpv-attachment">
            <div className="jpv-attachment-icon">
              <FileText size={20} />
            </div>
            <div className="jpv-attachment-info">
              <div className="jpv-attachment-name">{getFileName(mainAttachment)}</div>
              <div className="jpv-attachment-type">Job Description Document</div>
            </div>
            <a
              href={getFileUrl(mainAttachment)}
              target="_blank"
              rel="noopener noreferrer"
              className="jpv-view-btn"
            >
              <Download size={13} /> View
            </a>
          </div>
        ) : (
          <p className="jpv-no-attachment">
            <FileText size={14} /> No Job Description Attached
          </p>
        )}

        {formData.otherFiles && formData.otherFiles.length > 0 && (
          <div className="jpv-other-files">
            <span className="jpv-other-label">Other Files:</span>
            <ul className="jpv-files-list">
              {Array.from(formData.otherFiles).map((file, index) => (
                <li key={index} className="jpv-file-item">
                  <FileText size={12} /> {file.name || file}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* ═══ External Link ═══ */}
      {formData.externalLink && (
        <div className="jpv-section jpv-cta-section">
          <a
            href={formData.externalLink}
            target="_blank"
            rel="noopener noreferrer"
            className="jpv-external-btn"
          >
            Learn More at Our Careers Page <ExternalLink size={15} />
          </a>
        </div>
      )}
    </div>
  );
};

export default JobPostingPreview;